import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import {
  PROMPT,
  formatEventDate,
  loadBriefDraft,
  type BriefDraft,
} from '../../lib/briefDraft';
import { trackEvent } from '../../lib/posthog';

/**
 * "Pick up where you left off" — a quiet nudge back into an unfinished brief.
 *
 * Deliberately not a modal: there is no scrim and nothing is trapped, so the
 * page stays scrollable and usable underneath. It retires itself after a few
 * seconds (the thin bar under the card tracks that), tries again twice, then
 * leaves the visitor alone.
 *
 * Web: a card in the top-left gutter. Mobile: a quarter-height sheet that
 * slides up from the bottom edge.
 */

const EASE_OUT = [0.16, 1, 0.3, 1] as const;
/**
 * The app's accent yellow — the same one used for verified marks, focus rings
 * and the archive card badges. Shared by the progress ring and the countdown
 * bar so the prompt speaks one colour.
 */
const ACCENT = '#E9B306';
const RING_SIZE = 52;
const RING_RADIUS = 23;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ResumeBriefPrompt: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [draft, setDraft] = useState<BriefDraft | null>(null);
  /**
   * `full` is the card, `pill` is the small chip it shrinks into, `hidden` is
   * before the first appearance or after a dismissal. The pill persists for as
   * long as the brief is unfinished, so resuming is always one tap away and
   * nobody has to wait out the gap.
   */
  const [mode, setMode] = useState<'hidden' | 'full' | 'pill'>('hidden');
  const shownCount = useRef(0);
  const timers = useRef<number[]>([]);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  // Never interrupt the brief itself, and stay out of the admin console.
  const suppressed =
    location.pathname.startsWith('/order') || location.pathname.startsWith('/mgt-x7k9p2');

  const expand = useCallback((delay: number) => {
    const id = window.setTimeout(() => {
      const current = loadBriefDraft();
      if (!current) return;

      setDraft(current);
      setMode('full');
      shownCount.current += 1;
      trackEvent('resume_brief_prompt_shown', {
        step: current.stepIdx + 1,
        appearance: shownCount.current,
      });

      // Shrink rather than disappear: the pill keeps the brief one tap away,
      // and the full card returns after the gap.
      const shrink = window.setTimeout(() => {
        setMode('pill');
        expand(PROMPT.repeatEveryMs);
      }, PROMPT.visibleMs);
      timers.current.push(shrink);
    }, delay);
    timers.current.push(id);
  }, []);

  useEffect(() => {
    clearTimers();

    const current = loadBriefDraft();
    if (suppressed || !current) {
      setMode('hidden');
      return;
    }

    // The pill appears as soon as there is a brief to resume — it is small and
    // unobtrusive, and leaving someone without the shortcut after they have
    // already been to the brief is the worse failure. The full card still
    // waits, so arrival is never interrupted.
    setDraft(current);
    setMode('pill');
    expand(PROMPT.firstDelayMs);

    return clearTimers;
  }, [suppressed, expand]);

  /** Closing the card drops it to the pill — the brief is never lost. */
  const collapse = () => {
    clearTimers();
    setMode('pill');
    expand(PROMPT.repeatEveryMs);
    trackEvent('resume_brief_prompt_collapsed', { step: (draft?.stepIdx ?? 0) + 1 });
  };

  const resume = () => {
    clearTimers();
    setMode('hidden');
    trackEvent('resume_brief_prompt_resumed', { step: (draft?.stepIdx ?? 0) + 1 });
    navigate('/order', { state: { resume: true, from: location.pathname } });
  };

  if (!draft) return null;

  const stepNumber = draft.stepIdx + 1;
  const stepsLeft = Math.max(0, draft.totalSteps - stepNumber);
  const progress = Math.min(1, draft.stepIdx / draft.totalSteps);
  const meta = [draft.title, draft.fields.occasion, formatEventDate(draft.fields.eventDate)]
    .filter(Boolean)
    .join(' · ');

  const card = (
    <div className="w-full bg-white rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.14)] border border-gray-100 overflow-hidden">
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* The piece, held inside a ring that fills as the brief progresses. */}
          <span className="relative flex-shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
            <svg width={RING_SIZE} height={RING_SIZE} className="absolute inset-0 -rotate-90">
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke="#E8EDE9"
                strokeWidth="3"
              />
              <circle
                cx={RING_SIZE / 2}
                cy={RING_SIZE / 2}
                r={RING_RADIUS}
                fill="none"
                stroke={ACCENT}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
              />
            </svg>
            <span className="absolute inset-[6px] rounded-full overflow-hidden bg-gray-100">
              {draft.imageUrl && (
                <img
                  src={draft.imageUrl}
                  alt=""
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
            </span>
          </span>

          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-bold text-gray-900 leading-snug">Pick up where you left off</p>
            {meta && <p className="text-[13px] text-gray-500 leading-snug mt-0.5">{meta}</p>}
          </div>

          <button
            type="button"
            onClick={collapse}
            aria-label="Minimise"
            className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <X size={14} />
          </button>
        </div>

        {/* Step dashes + how much is left */}
        <div className="flex items-center gap-3 mt-3">
          <span className="flex items-center gap-1.5 flex-shrink-0">
            {Array.from({ length: draft.totalSteps }, (_, i) => (
              <span
                key={i}
                className={`h-[3px] w-5 rounded-full ${i < stepNumber ? 'bg-gray-900' : 'bg-gray-200'}`}
              />
            ))}
          </span>
          <p className="text-[12px] text-gray-500 truncate">
            Step {stepNumber} of {draft.totalSteps}
            {stepsLeft > 0 && ` · ${stepsLeft} step${stepsLeft === 1 ? '' : 's'} left`}
          </p>
        </div>

        <button
          type="button"
          onClick={resume}
          className="w-full mt-3 py-3 rounded-full bg-gray-900 text-white text-[14px] font-bold hover:bg-black active:scale-[0.98] transition-all"
        >
          Continue brief
        </button>
      </div>

      {/* How long the card has left before it retires itself. */}
      <motion.div
        key={shownCount.current}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: PROMPT.visibleMs / 1000, ease: 'linear' }}
        className="h-[3px] origin-left"
        style={{ backgroundColor: ACCENT }}
      />
    </div>
  );

  /**
   * The shrunk form — enough to recognise the brief, small enough to ignore.
   * No close button by design: once it is down to the pill, the way back to an
   * unfinished commission stays available.
   */
  const pill = (
    <div className="inline-flex items-center gap-2.5 pl-2 pr-3.5 py-2 rounded-full bg-white border border-gray-100 shadow-[0_8px_28px_rgba(0,0,0,0.16)]">
      <button
        type="button"
        onClick={resume}
        className="flex items-center gap-2.5 min-w-0 group"
        aria-label={`Continue your brief — step ${stepNumber} of ${draft.totalSteps}`}
      >
        <span className="relative flex-shrink-0 w-8 h-8">
          <svg width="32" height="32" className="absolute inset-0 -rotate-90">
            <circle cx="16" cy="16" r="14" fill="none" stroke="#EFEAE3" strokeWidth="2.5" />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke={ACCENT}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray={2 * Math.PI * 14}
              strokeDashoffset={2 * Math.PI * 14 * (1 - progress)}
            />
          </svg>
          <span className="absolute inset-[4px] rounded-full overflow-hidden bg-gray-100">
            {draft.imageUrl && (
              <img
                src={draft.imageUrl}
                alt=""
                className="w-full h-full object-cover"
                // A device photo's blob URL dies with the page that made it;
                // show the plain circle rather than a broken-image glyph.
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            )}
          </span>
        </span>
        <span className="text-[12px] font-semibold text-gray-900 whitespace-nowrap">
          Pick up where you left off
          <span className="font-normal text-gray-500"> · {stepsLeft} left</span>
        </span>
      </button>
    </div>
  );

  return (
    <AnimatePresence mode="wait">
      {mode === 'pill' && (
        <>
          <motion.div
            key="pill-web"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.24, ease: EASE_OUT }}
            className="hidden md:block fixed right-5 top-24 z-[55] origin-top-right"
          >
            {pill}
          </motion.div>
          <motion.div
            key="pill-mobile"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.24, ease: EASE_OUT }}
            className="md:hidden fixed right-3 bottom-20 z-[55] origin-bottom-right"
            style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
          >
            {pill}
          </motion.div>
        </>
      )}

      {mode === 'full' && (
        <>
          {/* Web — right-hand gutter, where the eye already rests, and out of
              the content's way. */}
          <motion.div
            key="resume-web"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16 }}
            transition={{ duration: 0.32, ease: EASE_OUT }}
            className="hidden md:block fixed right-5 top-24 z-[55] w-[340px]"
            role="status"
          >
            {card}
          </motion.div>

          {/* Mobile — a quarter-height sheet on the bottom edge. */}
          <motion.div
            key="resume-mobile"
            initial={{ y: '110%' }}
            animate={{ y: 0 }}
            exit={{ y: '110%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320, mass: 0.9 }}
            className="md:hidden fixed inset-x-3 bottom-20 z-[55]"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            role="status"
          >
            {card}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ResumeBriefPrompt;
